import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { getMeasurementMetric, getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { Measurement, RollupFunction, WindowFunction } from '@kentik/ui-shared/query/query.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { getDefaultRollupFn, getWindowFn, ROLLUP_OPTIONS } from '@kentik/ui-shared/query/query';
import { Box, Button, Flex, Text } from 'core/components';
import { SortableMenu } from 'app/views/boards/panels/ude/SortableMenu';
import { FilterGroupsField, FilterGroupState } from 'app/views/boards/panels/ude/filters/filter.types';

import MetricDimensionSelector from './MetricDimensionSelector';
import { usePopoverGate } from './PopoverGateContext';
import { useUdeForm } from './useUdeForm';

const PRESET_PERCENTILES = new Set(ROLLUP_OPTIONS.filter((opt) => opt.value.startsWith('p')).map((opt) => opt.value));

interface UdePanelMetricsProps {
  measurements: Measurement[];
  appProtocols?: AppProtocolsDictionary;
  headerVariant?: 'default' | 'pane';
}

const UdePanelMetrics = observer(({ measurements, appProtocols, headerVariant }: UdePanelMetricsProps) => {
  const form = useUdeForm();
  const { isOpen: isAddingMetric, setOpen: setAddingMetric } = usePopoverGate('metrics');

  const measurement = form.getValue('config.data.measurement');
  const metrics = form.getValue('config.data.metrics') || [];
  const dimensions = form.getValue('config.data.dimensions') || [];
  const lookback = form.getValue('config.data.time.lookback');

  const measurementData = measurement ? getMeasurementSync(measurement, measurements) : null;

  const getMetricData = useCallback(
    (metricName: string) => {
      if (measurementData) {
        return getMeasurementMetric(measurementData, metricName);
      }
      return null;
    },
    [measurementData]
  );

  const getWindowFunctionForMetric = useCallback(
    (metricName: string, rollupFn?: RollupFunction): WindowFunction => getWindowFn(getMetricData(metricName), rollupFn),
    [getMetricData]
  );

  const handleOpenAddMetric = useCallback(() => setAddingMetric(true), [setAddingMetric]);
  const handleCloseAddMetric = useCallback(() => setAddingMetric(false), [setAddingMetric]);

  const getRollupFnsForMetric = useCallback(
    (metricName: string): RollupFunction[] => {
      const rollups = form.getValue('config.data.rollups') || [];
      const fns = rollups.filter((r) => r.metric === metricName).map((r) => r.fn as RollupFunction);
      return fns.length ? fns : ['avg'];
    },
    [form]
  );

  const handleRollupChange = useCallback(
    (metricName: string, newFns: RollupFunction[]) => {
      const currentRollups = form.getValue('config.data.rollups') || [];
      const otherRollups = currentRollups.filter((r) => r.metric !== metricName);
      const newEntries = newFns.map((fn) => ({ metric: metricName, fn }));
      form.setValue('config.data.rollups', [...otherRollups, ...newEntries]);

      const primaryFn = newFns[0] || 'avg';
      const currentWindowFn = form.getValue('config.data.window.fn') || {};
      form.setValue('config.data.window.fn', {
        ...currentWindowFn,
        [metricName]: getWindowFunctionForMetric(metricName, primaryFn)
      });
    },
    [form, getWindowFunctionForMetric]
  );

  const handleRemoveMetric = useCallback(
    (metricName: string) => {
      const currentMetrics = form.getValue('config.data.metrics') || [];
      const updatedMetrics = currentMetrics.filter((metric) => metric.name !== metricName);
      form.setValue('config.data.metrics', updatedMetrics);
      const currentRollups = form.getValue('config.data.rollups') || [];
      const updatedRollups = currentRollups.filter((rollup) => rollup.metric !== metricName);
      form.setValue('config.data.rollups', updatedRollups);
      // Also clean up window.fn
      const currentWindowFn = form.getValue('config.data.window.fn') || {};
      const { [metricName]: _, ...updatedWindowFn } = currentWindowFn;
      form.setValue('config.data.window.fn', updatedWindowFn);
      // Clean up sort if it was sorting by this metric
      // Rollup format is fn_metric (e.g., "avg_latency"), extract metric portion for exact match
      const currentSort = form.getValue('config.data.sort') || [];
      const updatedSort = currentSort.filter(
        (s) =>
          // Rollup format is fn_metricName — but metric names can contain underscores,
          // so match against the known metric name at the end of the rollup string
          !s.rollup.endsWith(`_${metricName}`) && s.rollup !== metricName
      );
      form.setValue('config.data.sort', updatedSort);

      // Clean up metric filters that reference the removed metric
      // Use getField to access the form field state (not getValue which returns plain data)
      const filterGroups = form.getField('config.data.filters.metrics.filterGroups') as FilterGroupsField | undefined;
      if (filterGroups) {
        // Collect indices to remove (in reverse order to avoid index shifting issues)
        const groupsToRemove: number[] = [];
        filterGroups.forEach((group: FilterGroupState, groupIndex: number) => {
          // Collect filter indices to remove within this group
          const filtersToRemove: number[] = [];

          group.filters.fieldStates.forEach((filter, filterIndex) => {
            const filterField = filter.filterField.getValue();
            const metricField = filter.metric?.getValue?.() || '';

            if (filterField === metricName || metricField === metricName) {
              filtersToRemove.push(filterIndex);
            }
          });

          // Remove filters in reverse order to maintain correct indices
          filtersToRemove.reverse().forEach((filterIndex) => {
            group.filters.remove(filterIndex);
          });

          // Check if group is now empty and should be removed
          const remainingFilters = group.filters.fieldStates?.length || 0;
          if (remainingFilters === 0) {
            groupsToRemove.push(groupIndex);
          }
        });

        // Remove empty groups in reverse order
        groupsToRemove.reverse().forEach((groupIndex) => {
          filterGroups.remove(groupIndex);
        });
      }
    },
    [form]
  );

  const handleAddMetric = useCallback(
    (metricValue: string) => {
      const currentMetrics = form.getValue('config.data.metrics') || [];
      if (currentMetrics.find((metric) => metric.name === metricValue)) {
        return;
      }

      form.setValue('config.data.metrics', [...currentMetrics, { name: metricValue }]);

      // Check if rollup is needed from MEASUREMENTS_DATA
      const metricDetails = getMetricData(metricValue);
      if (metricDetails && !metricDetails.Aggregate) {
        const currentRollups = form.getValue('config.data.rollups') || [];
        if (!currentRollups.find((rollup) => rollup.metric === metricValue)) {
          // Use appropriate default rollup function based on metric unit type
          const defaultFn = getDefaultRollupFn(metricDetails);
          form.setValue('config.data.rollups', [...currentRollups, { metric: metricValue, fn: defaultFn }]);

          // Also set window.fn so SQL query uses the correct aggregation
          // If the metric has a Window function defined, use that; otherwise use the default rollup
          const currentWindowFn = form.getValue('config.data.window.fn') || {};
          form.setValue('config.data.window.fn', {
            ...currentWindowFn,
            [metricValue]: getWindowFunctionForMetric(metricValue, defaultFn)
          });
        }
      }
    },
    [form, getMetricData, getWindowFunctionForMetric]
  );

  const handleReorder = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      const currentMetrics = form.getValue('config.data.metrics') || [];
      const updatedMetrics = Array.from(currentMetrics);
      const [movedMetric] = updatedMetrics.splice(oldIndex, 1);
      updatedMetrics.splice(newIndex, 0, movedMetric);
      form.setValue('config.data.metrics', updatedMetrics);
    },
    [form]
  );

  const hasMeasurement = !!measurement;
  const measurementObj = hasMeasurement ? getMeasurementSync(measurement, measurements) : null;
  const availableMetricCount = Object.keys(measurementObj?.storage?.Metrics ?? {}).length;
  const showAddButton = hasMeasurement && !(availableMetricCount === 1 && metrics.length >= 1);

  const allRollups = form.getValue('config.data.rollups') || [];
  const customPercentiles = [
    ...new Set(
      allRollups.map((r) => r.fn as RollupFunction).filter((fn) => fn.startsWith('p') && !PRESET_PERCENTILES.has(fn))
    )
  ];

  const content = (
    <Flex flexDirection="column" px="12px" pt={0} pb={0} display="flex" gap={1}>
      {!metrics?.length && (
        <Text muted small fontWeight="normal">
          No units selected
        </Text>
      )}
      {metrics?.length > 0 && (
        <SortableMenu
          measurement={form.getValue('config.data.measurement') || ''}
          handleRemoveItem={handleRemoveMetric}
          type="metric"
          measurements={measurements}
          items={metrics}
          onSortEnd={handleReorder}
          getRollupFns={getRollupFnsForMetric}
          onRollupChange={handleRollupChange}
          customPercentiles={customPercentiles}
        />
      )}
    </Flex>
  );

  const addButton = showAddButton ? (
    <Box ml="auto">
      <MetricDimensionSelector
        type="metric"
        measurement={measurement}
        measurements={measurements}
        selectedItems={metrics}
        selectedDimensions={dimensions}
        appProtocols={appProtocols}
        isOpen={isAddingMetric}
        onClose={handleCloseAddMetric}
        onSelect={handleAddMetric}
        onRemove={handleRemoveMetric}
        measurementLookback={lookback}
        keepOpenOnSelect
      >
        {({ isOpen }) => (
          <Button small minimal intent="primary" text="Add" onClick={handleOpenAddMetric} active={isOpen} />
        )}
      </MetricDimensionSelector>
    </Box>
  ) : null;

  return headerVariant === 'pane' ? (
    <>
      <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
        <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
          Units
        </Text>
        {addButton}
      </Flex>
      {content}
    </>
  ) : (
    <Flex flexDirection="column" pb="12px">
      <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
        <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
          Units
        </Text>
        {addButton}
      </Flex>
      {content}
    </Flex>
  );
});

export default UdePanelMetrics;
